// utils/advanceSeason.js

import { db } from "../lib/firebase";
import {
  computeSeasonStartPrices,
} from "../utils/seasonPriceEngine";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { getLeagueConfig } from "../utils/leagueConfig";

/**
 * advanceSeason
 * -----------------------------------
 * Creates Season (N+1) for a league.
 *
 * Steps:
 * 1. Read current season ID from league doc
 * 2. Determine next season number
 * 3. Create new season document
 * 4. Seed new players collection
 * 5. Create GW1
 * 6. Reset each user's team doc for new season
 */
export async function advanceSeason({
  leagueID,
  priceRule = "original", // "original" | "manual" | "auto" (future)
  matchDay,
  timeOfDay,
  chipsEnabled,
  manualPlayers,
}) {
  const normalizedMatchDay =
    typeof matchDay === "string" ? matchDay : matchDay?.value;
  const normalizedTimeOfDay =
    typeof timeOfDay === "string" ? timeOfDay : timeOfDay?.value;

  try {
    // Validate required season settings
    if (!normalizedMatchDay || !normalizedTimeOfDay) {
      throw new Error(
        "Season settings missing: matchDay or timeOfDay not provided."
      );
    }
    if (!priceRule) {
      throw new Error("Season settings missing: priceRule not provided.");
    }
    if (chipsEnabled === undefined || chipsEnabled === null) {
      throw new Error("Season settings missing: chipsEnabled not provided.");
    }
    const leagueRef = doc(db, "leagues", leagueID);
    const leagueSnap = await getDoc(leagueRef);

    if (!leagueSnap.exists()) {
      throw new Error("League not found during season migration");
    }

    const leagueData = leagueSnap.data();
    const currentSeasonID = leagueData.currentSeasonID || "S1";
    const leagueType = String(leagueData.leagueType || "5");

    // ------------------------------------
    // Auto price calculation (season rollover)
    // ------------------------------------
    let autoSeasonPrices = null;

    if (priceRule === "auto") {
      console.log("[AdvanceSeason] Computing auto season prices…");
      autoSeasonPrices = await computeSeasonStartPrices({
        leagueID,
      });
      console.log("[AdvanceSeason] Auto prices computed", autoSeasonPrices);
    }

    // ------------------------------------
    // Set old season's current GW to isCurrent: false
    // ------------------------------------
    if (leagueData.currentGW) {
      const oldGWRef = doc(
        db,
        "leagues",
        leagueID,
        "seasons",
        currentSeasonID,
        "gameweeks",
        leagueData.currentGW
      );
      try {
        await updateDoc(oldGWRef, { isCurrent: false });
      } catch (e) {
        console.error("Failed to update old season's current gameweek:", e);
      }
    }

    // Convert "S1" -> 1 -> 2 -> "S2"
    const currentSeasonNum = parseInt(currentSeasonID.replace("S", ""), 10);
    const nextSeasonNum = currentSeasonNum + 1;
    const nextSeasonID = `S${nextSeasonNum}`;

    console.log(
      `Advancing ${leagueID} from ${currentSeasonID} to ${nextSeasonID}…`
    );

    // ------------------------------------
    // 1. Create Season (N+1)
    // ------------------------------------
    const newSeasonRef = doc(
      collection(db, "leagues", leagueID, "seasons"),
      nextSeasonID
    );

    await setDoc(newSeasonRef, {
      seasonID: nextSeasonNum,
      createdAt: new Date(),
      priceRule,
      matchDay: normalizedMatchDay,
      timeOfDay: normalizedTimeOfDay,
      chipsEnabled,
    });

    // ------------------------------------
    // 2. Clone Players for new season
    // ------------------------------------
    const oldPlayersRef = collection(
      db,
      "leagues",
      leagueID,
      "seasons",
      currentSeasonID,
      "players"
    );
    const oldPlayersSnap = await getDocs(oldPlayersRef);

    const newPlayersRef = collection(
      db,
      "leagues",
      leagueID,
      "seasons",
      nextSeasonID,
      "players"
    );

    let manualPlayersMap = null;
    if (priceRule === "manual" && Array.isArray(manualPlayers)) {
      manualPlayersMap = {};
      for (const mp of manualPlayers) {
        if (mp.id) {
          manualPlayersMap[mp.id] = mp;
        }
      }
    }

    for (const playerDoc of oldPlayersSnap.docs) {
      const p = playerDoc.data();
      const playerID = playerDoc.id;

      let newPrice;
      let position;
      let rating;

      if (priceRule === "manual") {
        const manualPlayer = manualPlayersMap?.[playerID];
        if (!manualPlayer) {
          throw new Error(
            `Manual price missing for player ${playerID} during season rollover`
          );
        }
        newPrice = manualPlayer.newPrice;
        position = manualPlayer.position;
        rating = manualPlayer.rating;
      } else if (priceRule === "auto") {
        const engineResult = autoSeasonPrices?.pricesByPlayer?.[playerID];
        if (!engineResult) {
          throw new Error(
            `Auto price missing for player ${playerID} during season rollover`
          );
        }
        newPrice = engineResult.newOriginal;
        position = p.position;
        rating = p.rating;
      } else {
        newPrice = p.originalPrice;
        position = p.position;
        rating = p.rating;
      }

      await setDoc(doc(newPlayersRef, playerID), {
        id: playerID,
        name: p.name,
        position,
        rating,
        originalPrice: newPrice,
        currentPrice: newPrice,
        createdAt: new Date(),
        priceHistory: { GW1: newPrice },
        pointsHistory: {},
        joinedGW: "GW1",
        priceAccumulator: 0,
        priceDelta: 0,
      });
    }


    const config = getLeagueConfig(leagueType);
    const openingBudget = (config && typeof config.budget === "number") ? config.budget : 24.0;

    // ------------------------------------
    // 3. Create GW1 for the new season
    // ------------------------------------
    const gw1Ref = doc(
      collection(
        db,
        "leagues",
        leagueID,
        "seasons",
        nextSeasonID,
        "gameweeks"
      ),
      "GW1"
    );

    const gw1Data = {
      id: "GW1",
      number: 1,
      phase: "pick",
      isCurrent: true,
      deadline: calculateDeadline(normalizedMatchDay, normalizedTimeOfDay),
      createdAt: new Date(),
      matchDay: normalizedMatchDay,
      timeOfDay: normalizedTimeOfDay,
    };
    await setDoc(gw1Ref, gw1Data);

    const gw1TeamsRef = collection(
      db,
      "leagues",
      leagueID,
      "seasons",
      nextSeasonID,
      "gameweeks",
      "GW1",
      "teams"
    );

    // ------------------------------------
    // Create GW1/team docs
    // ------------------------------------
    const teamsRef = collection(
      db,
      "leagues",
      leagueID,
      "seasons",
      currentSeasonID,
      "teams"
    );
    const teamsSnap = await getDocs(teamsRef);

    for (const tDoc of teamsSnap.docs) {
      const teamID = tDoc.id;
      const tData = tDoc.data();

      const newGW1TeamData = {
        leagueID,
        teamID,
        managerID: tData.managerID || tData.userID || "",
        startGW: "GW1",
        selections: { 
          GW1: {            
            DEF: [],
            MID: [],
            FWD: [],
          }
        },
        captains: { GW1: "" },
        chipsUsed: { GW1: "None" },
        transfersFree: { GW1: "unlimited" },
        currentBudget: {GW1: openingBudget},
        transfersMade: { GW1: 0 },
        lastSavedAt: new Date(),
        bankStart: { GW1: openingBudget },
        squadMeta: {
          GW1: {
            DEF: {},
            MID: {},
            FWD: {},
          },
        },
      };
      await setDoc(doc(gw1TeamsRef, teamID), newGW1TeamData);
    }

    // ------------------------------------
    // 4. Reset user team docs for the new season
    // (team doc is NOT recreated — we keep the same teamID)
    // ------------------------------------

    for (const tDoc of teamsSnap.docs) {
      const teamID = tDoc.id;
      const tData = tDoc.data();

      const seasonTeamRef = doc(
        db,
        "leagues",
        leagueID,
        "seasons",
        nextSeasonID,
        "teams",
        teamID
      );

      const newSeasonTeamData = {
        managerID: tData.managerID || tData.userID || "",
        leagueID,
        leagueType,
        name: tData.name || "",
        createdAt: new Date(),
        captains: {GW1: "" },
        chipsUsed: {GW1: "None" },
        currentBudget: {GW1: openingBudget},
        selections: { 
          GW1: {            
            DEF: [],
            MID: [],
            FWD: [],
          }
        },
        squadMeta: {
          GW1: {
            DEF: [],
            MID: [],
            FWD: [],
          },
        },
        transfersMade: { GW1: 0 },
        transfersFree: { GW1: "unlimited" },
        maxFreeTransfers: leagueData.maxFreeTransfers,
        startGW: "GW1",
        bankStart: { GW1: openingBudget },
      };
      await setDoc(seasonTeamRef, newSeasonTeamData);
    }

    // ------------------------------------
    // 5. Update the league root doc
    // ------------------------------------
    await updateDoc(leagueRef, {
      currentSeasonID: nextSeasonID,
      seasonMigratedAt: new Date(),
      matchDay: normalizedMatchDay,
      timeOfDay: normalizedTimeOfDay,
      currentGW: "GW1",
    });

    console.log(`Season ${nextSeasonID} created successfully.`);

    return {
      success: true,
      nextSeasonID,
    };
  } catch (err) {
    console.error("Error advancing season:", err);
    return {
      success: false,
      error: err.message,
    };
  }
}

// ------------------------
// Deadline Calculator
// ------------------------
function calculateDeadline(matchDay, timeOfDay) {
  // Convert matchDay + timeOfDay → real Date
  // Same logic as CreateLeague
  try {
    const now = new Date();
    const dayIndex = [
      "Sunday",
      "Monday",
      "Tuesday",
      "Wednesday",
      "Thursday",
      "Friday",
      "Saturday",
    ].indexOf(matchDay);

    const timeMap = {
      Daytime: { hours: 9, minutes: 0 },
      Evening: { hours: 14, minutes: 0 },
    };

    const nextDate = new Date(now);
    nextDate.setDate(now.getDate() + ((dayIndex - now.getDay() + 7) % 7));

    nextDate.setHours(timeMap[timeOfDay].hours);
    nextDate.setMinutes(timeMap[timeOfDay].minutes);
    nextDate.setSeconds(0);

    return nextDate;
  } catch {
    return new Date();
  }
}